import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Button, Dimmer, Form, Icon, Loader, Message, Radio, Table } from 'semantic-ui-react';
import store from '../../../store';
import { formatDateTime, toS3Url } from '../../../utils/shared.utils';
import { printReturnReceipts } from '../../utils/returnReceiptPrint';
import './return.scss';

interface ImportRow {
    id: number;
    warehouseId: number;
    productId: number;
    cost: number | string;
    remaining: number;
    originalCount: number;
    note?: string;
    expireDate?: string;
    createdAt?: string;
    createdBy?: string;
}

interface ProductState {
    productId: number;
    warehouseId: number | null;
    productName: string;
    slug?: string;
    providerName?: string;
    imageXsUrl?: string;
    warehouseCount?: number;
    imports: ImportRow[];
    activeImportId: number | null;
    minusCount: string;
    note: string;
    loadError?: string;
}

interface ConfirmPageProps {
    returnBasketIds: number[];
}

/** Амжилттай бүртгэгдсэн буцаалт — нийлүүлэгч тус бүрд нэг баримт үүснэ. */
interface SubmittedReturn {
    returnIds: number[];
    returnedCount: number;
}

function ReturnConfirmPage(props: ConfirmPageProps) {
    const history = useHistory();
    const [productStates, setProductStates] = useState<Record<number, ProductState>>({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [submitted, setSubmitted] = useState<SubmittedReturn | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token || token === 'undefined') {
            store.dispatch({ type: 'redirectUrl', data: window.location.pathname });
            history.push('/');
            return;
        }
        if (props.returnBasketIds.length === 0) {
            setLoading(false);
            return;
        }
        loadAll(token);
    }, []);

    const loadAll = async (token: string) => {
        setLoading(true);
        const next: Record<number, ProductState> = {};
        try {
            await Promise.all(
                props.returnBasketIds.map(async (productId) => {
                    try {
                        const whRes = await fetch(process.env.REACT_APP_API_URL + 'mongol/product/' + productId + '/warehouse', {
                            method: 'GET',
                            headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
                        });
                        if (!whRes.ok) throw new Error('warehouse lookup failed');
                        const whJson = await whRes.json();
                        const warehouseId = whJson?.warehouse?.id;
                        if (!warehouseId) throw new Error('no warehouseId');

                        const detailRes = await fetch(process.env.REACT_APP_API_URL + 'mongol/warehouse/' + warehouseId, {
                            method: 'GET',
                            headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
                        });
                        if (!detailRes.ok) throw new Error('warehouse detail failed');
                        const detailJson = await detailRes.json();
                        const wh = detailJson?.warehouse || {};
                        const imports: ImportRow[] = Array.isArray(detailJson?.importsWithRemaining) ? detailJson.importsWithRemaining : [];
                        next[productId] = {
                            productId,
                            warehouseId,
                            productName: wh.name || ('#' + productId),
                            slug: wh.slug,
                            imageXsUrl: wh.imageXsUrl,
                            warehouseCount: wh.count,
                            imports,
                            activeImportId: imports.length === 1 ? imports[0].id : null,
                            minusCount: '',
                            note: '',
                        };
                    } catch (err) {
                        next[productId] = {
                            productId,
                            warehouseId: null,
                            productName: '#' + productId,
                            imports: [],
                            activeImportId: null,
                            minusCount: '',
                            note: '',
                            loadError: 'Ачаалахад алдаа гарлаа',
                        };
                    }
                })
            );
            setProductStates(next);
        } finally {
            setLoading(false);
        }
    };

    const updateProduct = (productId: number, patch: Partial<ProductState>) => {
        setProductStates((prev) => {
            const cur = prev[productId];
            if (!cur) return prev;
            return { ...prev, [productId]: { ...cur, ...patch } };
        });
    };

    const removeProduct = (productId: number) => {
        store.dispatch({ type: 'RETURN_BASKET_REMOVE', productId });
        setProductStates((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
        });
    };

    const productList = useMemo(
        () => props.returnBasketIds.map((id) => productStates[id]).filter(Boolean) as ProductState[],
        [props.returnBasketIds, productStates]
    );

    const validation = useMemo(() => {
        if (productList.length === 0) return { ok: false, reason: 'empty' };
        for (const p of productList) {
            if (p.loadError || !p.warehouseId) return { ok: false, reason: 'load', productId: p.productId };
            if (p.imports.length === 0) return { ok: false, reason: 'no-import', productId: p.productId };
            if (p.activeImportId == null) return { ok: false, reason: 'pick-import', productId: p.productId };
            const minus = parseInt(p.minusCount, 10) || 0;
            if (minus <= 0) return { ok: false, reason: 'count', productId: p.productId };
            const note = (p.note || '').trim();
            if (!note) return { ok: false, reason: 'note', productId: p.productId };
            const imp = p.imports.find((i) => i.id === p.activeImportId);
            if (!imp) return { ok: false, reason: 'pick-import', productId: p.productId };
            if (minus > Number(imp.remaining || 0)) return { ok: false, reason: 'over', productId: p.productId };
        }
        return { ok: true } as const;
    }, [productList]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validation.ok || submitting) return;
        const token = localStorage.getItem('adminToken');
        if (!token) return;

        const items = productList.map((p) => ({
            warehouseId: p.warehouseId,
            decreaseItems: [
                {
                    warehouseImportId: p.activeImportId,
                    minusCount: parseInt(p.minusCount, 10) || 0,
                    note: (p.note || '').trim(),
                },
            ],
        }));

        setSubmitting(true);
        setErrorMsg('');
        try {
            const res = await fetch(process.env.REACT_APP_API_URL + 'mongol/return', {
                method: 'POST',
                headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ items }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setErrorMsg(data?.error || 'Алдаа гарлаа');
                return;
            }
            // Сагс хоосорсны дараа хоосон төлөв биш амжилтын төлөв харагдахын тулд эхэлж тавина
            setSubmitted({
                returnIds: Array.isArray(data?.returnIds) ? data.returnIds : [],
                returnedCount: Number(data?.returnedCount) || 0,
            });
            store.dispatch({ type: 'RETURN_BASKET_CLEAR' });
        } catch (err) {
            setErrorMsg('Сервертэй холбогдоход алдаа гарлаа.');
        } finally {
            setSubmitting(false);
        }
    };

    // Popup-ыг click дотор синхрон нээх ёстой тул onClick-ээс шууд дуудна.
    const printSubmittedReceipts = () => {
        const token = localStorage.getItem('adminToken');
        if (!token || !submitted) return;
        setErrorMsg('');
        printReturnReceipts({ apiUrl: process.env.REACT_APP_API_URL || '', token, returnIds: submitted.returnIds }).then((result) => {
            if (!result.ok && result.error === 'network') setErrorMsg('Баримт ачаалахад алдаа гарлаа.');
        });
    };

    if (submitted) {
        const receiptCount = submitted.returnIds.length;
        return (
            <div className="page-admin admin-return-confirm">
                <section className="admin-return-section">
                    <h1>Бараа буцаах</h1>
                    <div className="return-success" data-testid="return-confirm-success">
                        <Message positive>
                            <Message.Header>
                                <Icon name="checkmark" /> Буцаалт амжилттай бүртгэгдлээ
                            </Message.Header>
                            <p>{submitted.returnedCount} ширхэг бараа буцаагдаж, нийлүүлэгчид имэйл илгээгдэв.</p>
                            {receiptCount > 1 && <p>Нийлүүлэгч тус бүрд тусдаа баримт үүслээ ({receiptCount} баримт).</p>}
                        </Message>
                        {errorMsg && (
                            <Message negative size="small">
                                <Icon name="warning circle" /> {errorMsg}
                            </Message>
                        )}
                        <div className="flex-div return-success-actions">
                            {receiptCount > 0 && (
                                <Button type="button" color="blue" onClick={printSubmittedReceipts} data-testid="return-confirm-print">
                                    <Icon name="print" /> Буцаалтын баримт хэвлэх
                                </Button>
                            )}
                            <Button type="button" basic onClick={() => history.push('/mongol/return/history')} data-testid="return-confirm-history">
                                <Icon name="history" /> Буцаалтын түүх
                            </Button>
                            <Button type="button" basic onClick={() => history.push('/mongol/return')} data-testid="return-confirm-new">
                                <Icon name="plus" /> Шинэ буцаалт
                            </Button>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    if (props.returnBasketIds.length === 0 && !loading) {
        return (
            <div className="page-admin admin-return-confirm">
                <section className="admin-return-section">
                    <div className="form-group">
                        <Link to="/mongol/return"><Icon name="arrow left" /><span className="underline">Буцах</span></Link>
                    </div>
                    <h1>Бараа буцаах</h1>
                    <div className="return-empty" data-testid="return-confirm-empty">
                        <p>Буцаах бараа сонгоогүй байна.</p>
                        <Link className="underline" to="/mongol/return">Бүтээгдэхүүн сонгох</Link>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="page-admin admin-return-confirm">
            <Dimmer active={loading || submitting} inverted>
                <Loader size="medium" />
            </Dimmer>
            <section className="admin-return-section">
                <div className="form-group">
                    <Link to="/mongol/return"><Icon name="arrow left" /><span className="underline">Сагсанд буцах</span></Link>
                </div>
                <h1>Буцаалт баталгаажуулах</h1>
                <p className="muted">Бүтээгдэхүүн тус бүрд өртгийн мөр, тоо, тайлбараа оруулна уу.</p>

                {errorMsg && (
                    <Message negative size="small">
                        <Icon name="warning circle" /> {errorMsg}
                    </Message>
                )}

                <Form onSubmit={onSubmit}>
                    {productList.map((p) => {
                        const minusNum = parseInt(p.minusCount, 10) || 0;
                        const noteEmpty = !(p.note || '').trim();
                        const activeRow = p.activeImportId != null ? p.imports.find((i) => i.id === p.activeImportId) : null;
                        const overImport = activeRow ? minusNum > Number(activeRow.remaining || 0) : false;
                        const blockError = !!p.loadError || (minusNum > 0 && (noteEmpty || overImport));

                        return (
                            <div
                                key={p.productId}
                                className={'return-product-block' + (blockError ? ' error' : '')}
                                data-testid="return-confirm-block"
                                data-product-id={p.productId}
                            >
                                <div className="return-product-block-header">
                                    <div className="return-product-block-image">
                                        {p.imageXsUrl ? <img alt={p.productName} src={toS3Url(p.imageXsUrl)} /> : <Icon name="image" />}
                                    </div>
                                    <div className="return-product-block-title">
                                        <div className="name">{p.productName}</div>
                                        <div className="provider">Үлдэгдэл: {p.warehouseCount != null ? p.warehouseCount : '–'}</div>
                                    </div>
                                    <Button type="button" basic color="grey" size="tiny" onClick={() => removeProduct(p.productId)} data-testid="return-confirm-remove">
                                        <Icon name="trash" /> Хасах
                                    </Button>
                                </div>

                                {p.loadError && (
                                    <Message negative size="tiny">
                                        <Icon name="warning circle" /> {p.loadError}
                                    </Message>
                                )}

                                {!p.loadError && p.imports.length === 0 && (
                                    <Message warning size="tiny">
                                        <Icon name="info circle" /> Үлдэгдэлтэй өртөг бүртгэлгүй тул буцаах боломжгүй.
                                    </Message>
                                )}

                                {p.imports.length > 0 && (
                                    <>
                                        <Table compact striped className="admin-return-imports-table">
                                            <Table.Header>
                                                <Table.Row>
                                                    <Table.HeaderCell width="1">Сонгох</Table.HeaderCell>
                                                    <Table.HeaderCell>Огноо</Table.HeaderCell>
                                                    <Table.HeaderCell>Өртөг</Table.HeaderCell>
                                                    <Table.HeaderCell>Үлдэгдэл</Table.HeaderCell>
                                                    <Table.HeaderCell>Дуусах огноо</Table.HeaderCell>
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {p.imports.map((imp) => (
                                                    <Table.Row key={imp.id} data-testid="return-confirm-import-row" data-import-id={imp.id}>
                                                        <Table.Cell data-label="Сонгох">
                                                            <Radio
                                                                name={'imp-' + p.productId}
                                                                checked={p.activeImportId === imp.id}
                                                                onChange={() => updateProduct(p.productId, { activeImportId: imp.id })}
                                                                data-testid="return-confirm-import-radio"
                                                            />
                                                        </Table.Cell>
                                                        <Table.Cell data-label="Огноо">{imp.createdAt ? formatDateTime(new Date(imp.createdAt)) : '–'}</Table.Cell>
                                                        <Table.Cell data-label="Өртөг">{imp.cost != null ? Number(imp.cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₮' : '–'}</Table.Cell>
                                                        <Table.Cell data-label="Үлдэгдэл">{imp.remaining}</Table.Cell>
                                                        <Table.Cell data-label="Дуусах огноо">{imp.expireDate ? String(imp.expireDate).slice(0, 10) : '–'}</Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table>

                                        <Form.Group widths="equal">
                                            <div data-testid="return-confirm-count" className="field-wrap-equal">
                                                <Form.Input
                                                    label="Буцаах тоо *"
                                                    type="number"
                                                    min={1}
                                                    max={activeRow ? activeRow.remaining : undefined}
                                                    value={p.minusCount}
                                                    onChange={(_, d) => updateProduct(p.productId, { minusCount: String(d.value) })}
                                                    placeholder="0"
                                                    error={overImport}
                                                    disabled={p.activeImportId == null}
                                                />
                                            </div>
                                            <div data-testid="return-confirm-note" className="field-wrap-equal">
                                                <Form.Input
                                                    label="Тайлбар *"
                                                    value={p.note}
                                                    onChange={(_, d) => updateProduct(p.productId, { note: String(d.value) })}
                                                    placeholder="Буцаалтын тайлбар"
                                                    error={minusNum > 0 && noteEmpty}
                                                    disabled={p.activeImportId == null}
                                                />
                                            </div>
                                        </Form.Group>
                                        {overImport && activeRow && (
                                            <Message negative size="tiny">
                                                <Icon name="exclamation triangle" /> Буцаах тоо ({minusNum}) сонгосон өртгийн үлдэгдлээс ({activeRow.remaining}) хэтэрсэн байна.
                                            </Message>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="flex-div" style={{ gap: 8, marginTop: 16 }}>
                        <Button type="button" basic onClick={() => history.push('/mongol/return')} data-testid="return-confirm-add-more">
                            <Icon name="arrow left" /> Илүү бараа нэмэх
                        </Button>
                        <Button type="submit" color="red" disabled={!validation.ok || submitting} data-testid="return-confirm-submit">
                            <Icon name="check" /> Буцаалт баталгаажуулах
                        </Button>
                    </div>
                </Form>
            </section>
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    returnBasketIds: state.returnBasket?.productIds || [],
});

export default connect(mapStateToProps)(ReturnConfirmPage);
